/**
 * Host logic for the one-time announcement modal. On app open (once auth has settled
 * and we're on an /app/* route) it fetches the feed, picks the newest eligible unseen
 * announcement, shows it AT MOST ONCE per app-open, and records it as seen ON DISPLAY.
 * See specs/in-app-announcements/spec.md (Story 1).
 */

import { useEffect, useState } from 'react';
import { usePathname } from 'expo-router';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import { useAnnouncementsQuery } from './useAnnouncementsQuery';
import { selectAnnouncementToShow } from '../helpers/selectAnnouncementToShow.helper';
import {
  getSeenAnnouncementIds,
  markAnnouncementSeen,
} from '../helpers/announcementSeenStorage.helper';
import { ANNOUNCEMENT_ROUTE_PREFIX } from '../constants/announcements.constants';
import type { Announcement } from '../types/announcements.types';

/**
 * Module-level guard: once an announcement modal has been shown, we never show
 * another during this app session (survives /app/* navigation and query refetches).
 */
const sessionGuard = { shown: false };

/** Test helper — reset the once-per-session guard between cases. */
export function __resetAnnouncementSessionGuard(): void {
  sessionGuard.shown = false;
}

export interface UseAnnouncementsResult {
  visible: boolean;
  blocks: Announcement['blocks'];
  onDismiss: () => void;
}

export function useAnnouncements(): UseAnnouncementsResult {
  const { isLoaded } = useSupabaseAuth();
  const pathname = usePathname();
  const onAppRoute = typeof pathname === 'string' && pathname.startsWith(ANNOUNCEMENT_ROUTE_PREFIX);

  const enabled = isLoaded && onAppRoute && !sessionGuard.shown;
  const { data: feed } = useAnnouncementsQuery(enabled);

  const [seenIds, setSeenIds] = useState<string[] | null>(null);
  const [selected, setSelected] = useState<Announcement | null>(null);

  // Load the device's seen set once.
  useEffect(() => {
    let active = true;
    getSeenAnnouncementIds()
      .then((ids) => {
        if (active) setSeenIds(ids);
      })
      .catch(() => {
        if (active) setSeenIds([]);
      });
    return () => {
      active = false;
    };
  }, []);

  // Evaluate once everything is ready; show at most one and mark it seen on display.
  useEffect(() => {
    if (sessionGuard.shown || selected) return;
    if (!isLoaded || !onAppRoute) return;
    if (!feed || seenIds === null) return;

    const pick = selectAnnouncementToShow(feed, seenIds);
    if (!pick) return;

    sessionGuard.shown = true;
    setSelected(pick);
    void markAnnouncementSeen(pick.id); // mark-on-display (AC-1.5)
  }, [isLoaded, onAppRoute, feed, seenIds, selected]);

  return {
    visible: selected != null,
    blocks: selected?.blocks ?? [],
    onDismiss: () => setSelected(null),
  };
}
